import { formatPeriod, inferTemplate, isActive } from '../catalog';
import {
  type ConfigDoProduto,
  RELATORIOS_POR_PAGINA,
  type ReportBlock,
  type ReportJob,
  type ReportsGateway,
  type ReportSnapshot,
} from '../types';

const memory = new Map<string, ReportSnapshot>();
export function advanceDemoJob(
  job: ReportJob,
  blocos: readonly ReportBlock[],
  now = Date.now(),
): ReportJob {
  if (!isActive(job.status)) return job;
  const elapsed = Math.max(0, now - Date.parse(job.createdAt));
  const stage = Math.min(4, Math.floor(elapsed / 3200));
  const completed = elapsed >= 18000;
  const count = completed
    ? job.brief.blocks.length
    : Math.max(0, Math.floor((elapsed - 7200) / 1400));
  return {
    ...job,
    version: Math.floor(elapsed / 1000),
    stage,
    updatedAt: new Date(now).toISOString(),
    status: completed ? 'completed' : 'running',
    blocks: job.brief.blocks
      .slice(0, count)
      .map((id) => blocos.find((block) => block.id === id)!)
      .filter(Boolean),
  };
}
/** Demonstração no navegador, um armazenamento por produto e escritório. */
export function criarGatewayDemo(config: ConfigDoProduto): ReportsGateway {
  const key = (scope: string) => `arbitralis:demo-reports:v1:${config.produto}:${scope}`;
  const avancar = (job: ReportJob) => advanceDemoJob(job, config.blocosDaDemonstracao);
  function read(scope: string): ReportSnapshot {
    try {
      const raw = localStorage.getItem(key(scope));
      if (raw) {
        const parsed = config.esquemas.demoSnapshot.safeParse(JSON.parse(raw));
        if (parsed.success) return parsed.data;
      }
    } catch {
      /* Storage privado: sessão em memória. */
    }
    return memory.get(key(scope)) ?? { jobs: [], preferences: config.defaultPreferences };
  }
  function write(scope: string, snapshot: ReportSnapshot) {
    memory.set(key(scope), snapshot);
    try {
      localStorage.setItem(key(scope), JSON.stringify(snapshot));
    } catch {
      /* Apenas dados fictícios; sem bloquear o fluxo. */
    }
  }
  function update(scope: string, id: string, cancel = false) {
    const snapshot = read(scope);
    const job = snapshot.jobs.find((item) => item.id === id);
    if (!job) throw new Error('Relatório não encontrado neste espaço.');
    const updated =
      cancel && isActive(job.status)
        ? { ...job, status: 'cancelled' as const, version: job.version + 1 }
        : avancar(job);
    write(scope, {
      ...snapshot,
      jobs: snapshot.jobs.map((item) => (item.id === id ? updated : item)),
    });
    return updated;
  }
  return {
    async interpret(_scope, text) {
      const template = inferTemplate(config, text) ?? config.templates[0];
      const supported =
        !/piada|receita de|programar|senha|ignore.*regras|delete.*from|temperatura|previs[aã]o do tempo|traduzir|futebol/i.test(
          text,
        );
      return {
        supported,
        title: template.title,
        focus: template.id,
        blocks: template.blocks,
        summary: supported
          ? config.textos.resumoDaDemonstracao(text.slice(0, 600))
          : config.textos.foraDoEscopoDaDemonstracao,
      };
    },
    async list(scope, { busca, status, cursor }) {
      const snapshot = read(scope);
      const jobs = snapshot.jobs.map(avancar);
      write(scope, { ...snapshot, jobs });
      const termo = busca?.toLocaleLowerCase('pt-BR');
      const encontrados = jobs.filter(
        (job) =>
          (!status?.length || status.includes(job.status)) &&
          (!termo ||
            `${job.brief.title} ${formatPeriod(job.brief)}`
              .toLocaleLowerCase('pt-BR')
              .includes(termo)),
      );
      const inicio = Number(cursor ?? 0);
      const fim = inicio + RELATORIOS_POR_PAGINA;
      return {
        items: encontrados.slice(inicio, fim),
        nextCursor: fim < encontrados.length ? String(fim) : null,
        hasMore: fim < encontrados.length,
      };
    },
    async readPreferences(scope) {
      return read(scope).preferences;
    },
    async savePreferences(scope, preferences) {
      write(scope, { ...read(scope), preferences });
      return preferences;
    },
    async create(scope, rawBrief, id) {
      const brief = config.esquemas.brief.parse(rawBrief);
      const snapshot = read(scope);
      const existing = snapshot.jobs.find((job) => job.id === id);
      if (existing) return existing;
      if (snapshot.jobs.some((job) => isActive(avancar(job).status)))
        throw new Error('Aguarde o relatório em construção antes de iniciar outro.');
      const now = new Date().toISOString();
      const job: ReportJob = {
        id,
        workspaceId: scope,
        version: 0,
        createdAt: now,
        updatedAt: now,
        status: 'queued',
        stage: 0,
        brief,
        blocks: [],
        demo: true,
        notificationEnabled: false,
      };
      write(scope, { ...snapshot, jobs: [job, ...snapshot.jobs].slice(0, 50) });
      return job;
    },
    async get(scope, id) {
      return update(scope, id);
    },
    async cancel(scope, id) {
      return update(scope, id, true);
    },
  };
}
